import React, { useState, useEffect, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, X, Sunrise, Moon, Loader2, UserMinus } from "lucide-react";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function prettyDate(ds) {
  try {
    const d = new Date(ds + "T00:00:00");
    const t = todayStr();
    if (ds === t) return "Today";
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch { return ds; }
}

export default function ScheduleManager({ onClose, onSaved, initialDate, highlightName }) {
  const [checklists, setChecklists] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeCid, setActiveCid] = useState(null);
  const [date, setDate] = useState(initialDate || todayStr());
  const [assign, setAssign] = useState({}); // { staffName: Set(sectionId) }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cl }, { data: st }] = await Promise.all([api.get("/checklists"), api.get("/staff")]);
        setChecklists(cl.checklists);
        setStaff(st.filter((s) => s.role !== "owner"));
        setActiveCid(cl.checklists[0]?.id || null);
      } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAssignments = useCallback(async (cid, d) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/schedule?checklist_id=${cid}&date=${d}`);
      const map = {};
      (data.assignments || []).forEach((a) => { map[a.name] = new Set(a.sections || []); });
      setAssign(map);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => { loadAssignments(activeCid, date); }, [activeCid, date, loadAssignments]);

  const activeChecklist = checklists.find((c) => c.id === activeCid);
  const sections = activeChecklist?.assignable_sections || [];

  const toggleSection = (name, sid) => {
    setAssign((prev) => {
      const cur = new Set(prev[name] || []);
      cur.has(sid) ? cur.delete(sid) : cur.add(sid);
      return { ...prev, [name]: cur };
    });
  };

  const assignAll = (name) => {
    setAssign((prev) => {
      const cur = prev[name] || new Set();
      const full = cur.size === sections.length;
      return { ...prev, [name]: full ? new Set() : new Set(sections.map((s) => s.id)) };
    });
  };

  const removeFromShift = (name) => {
    setAssign((prev) => ({ ...prev, [name]: new Set() }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const assignments = staff
        .map((s) => ({ name: s.name, sections: Array.from(assign[s.name] || []) }))
        .filter((a) => a.sections.length > 0);
      await api.put("/schedule", { checklist_id: activeCid, date, assignments });
      toast.success(`${activeChecklist?.title} · ${prettyDate(date)} saved`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#171717] flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E] z-10">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#E11D2A]" /> Assign Crew</h3>
          <button onClick={onClose} data-testid="schedule-close-button" className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#E11D2A]" /></div>
        ) : (
          <div className="p-5">
            <p className="text-sm text-[#A3A3A3] mb-4">Pick a day and checklist, then choose the exact sections each employee works. Assign ahead of time for any future date — staff only see it on that day.</p>

            {/* Date picker */}
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Shift date</label>
              <div className="flex items-center gap-2">
                <input type="date" data-testid="schedule-date-input" value={date} min={todayStr()} onChange={(e) => setDate(e.target.value)}
                  className="rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A] text-[#F5F5F5]" />
                <span className="text-sm font-semibold text-[#E11D2A]">{prettyDate(date)}</span>
              </div>
            </div>

            {/* Checklist selector */}
            <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
              {checklists.map((c) => {
                const Icon = c.shift === "opening" ? Sunrise : Moon;
                const on = c.id === activeCid;
                return (
                  <button key={c.id} data-testid={`schedule-checklist-${c.id}`} onClick={() => setActiveCid(c.id)}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${on ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#0E0E0E] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}>
                    <Icon className="h-4 w-4" /> {c.title}
                  </button>
                );
              })}
            </div>

            {sections.length === 0 ? (
              <p className="text-sm text-[#6B6B6B] py-6 text-center">No assignable sections for this checklist.</p>
            ) : (
              <div className="space-y-3 mb-5">
                {staff.map((s) => {
                  const set = assign[s.name] || new Set();
                  const full = set.size === sections.length;
                  const highlight = highlightName && s.name.toLowerCase() === highlightName.toLowerCase();
                  return (
                    <div key={s.id} data-testid={`schedule-staff-${s.id}`}
                      className={`rounded-xl border bg-[#0E0E0E] px-4 py-3 transition-colors ${highlight ? "border-[#E11D2A] ring-1 ring-[#E11D2A]/50" : "border-[#2E2E2E]"}`}>
                      <div className="flex items-center justify-between mb-2.5">
                        <div>
                          <p className="font-medium text-sm">{s.name}{highlight && <span className="ml-2 text-[10px] font-semibold text-[#E11D2A] uppercase tracking-wide">requested</span>}</p>
                          <p className="text-xs text-[#6B6B6B] capitalize">{s.role}{s.area && s.area !== "all" ? ` · ${s.area === "foh" ? "Front of House" : s.area}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {set.size > 0 && (
                            <button data-testid={`schedule-remove-${s.id}`} onClick={() => removeFromShift(s.name)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-[#A3A3A3] hover:text-[#E11D2A] transition-colors" title="Remove from this shift">
                              <UserMinus className="h-3.5 w-3.5" /> Remove
                            </button>
                          )}
                          <button data-testid={`schedule-assignall-${s.id}`} onClick={() => assignAll(s.name)}
                            className="text-xs font-medium text-[#A3A3A3] hover:text-[#E11D2A] transition-colors">
                            {full ? "Clear all" : "All sections"}
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {sections.map((sec) => {
                          const on = set.has(sec.id);
                          return (
                            <button key={sec.id} data-testid={`schedule-section-${s.id}-${sec.id}`} onClick={() => toggleSection(s.name, sec.id)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#171717] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}>
                              {sec.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button data-testid="schedule-save-button" onClick={save} disabled={busy} className="w-full rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-3 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
              {busy ? "Saving…" : `Save ${activeChecklist?.title || ""} · ${prettyDate(date)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
